import { useEffect, useRef } from 'react';

import ToastWrapper from '@common/helpers/toastWrapper';
import { useTranslation } from '@common/hooks/translation';
import PaymentsService from '@app/dev/services/payments';

import {
  MPESA_PLAYER_STORAGE_KEY,
  PAYMENT_REFERENCE_QUERY_PARAM,
  PAYMENT_STATUS_QUERY_PARAM,
  PENDING_PAYMENT_STORAGE_KEY,
  normalisePaymentStatus,
} from '../../constants/paymentRedirect';

// KesPay redirects back to `<origin>/?status=success&reference=<id>`
// regardless of the `status_urls` we passed in the deposit/withdraw
// request (the provider uses a return URL configured on their side and
// appends the outcome as a query param). The path-based
// `PaymentCallbackBridge` route therefore never matches — this component
// is mounted near the top of the React tree (inside Layout) so it can
// observe the URL on mount and run the same restore + toast + cleanup
// flow regardless of which path the user landed on.
//
// Lives outside the path-based route so it works whether the redirect
// hits `/`, `/betting/...`, or whatever the partner has set as the
// merchant default in KesPay's portal.

const TOAST_BY_STATUS = {
  success: { kind: 'success', verb: 'successful' },
  fail: { kind: 'error', verb: 'failed' },
  cancel: { kind: 'error', verb: 'cancelled' },
  pending: { kind: 'info', verb: 'pending' },
};

const PaymentStatusListener = () => {
  const t = useTranslation();
  // One-shot guard so StrictMode / re-renders don't fire the toast twice.
  const handledRef = useRef(false);

  useEffect(() => {
    if (handledRef.current) return;
    if (typeof window === 'undefined') return;

    const params = new URLSearchParams(window.location.search);
    const rawStatus = params.get(PAYMENT_STATUS_QUERY_PARAM);
    const outcome = normalisePaymentStatus(rawStatus);
    if (!outcome) return;
    handledRef.current = true;

    // Restore M-Pesa player session that was stashed before the redirect.
    // Only useful in the same-tab redirect flow (sessionStorage is
    // per-tab); in the new-tab flow the original tab keeps its own
    // playerInfo and this stash is empty — harmless either way.
    try {
      const raw = sessionStorage.getItem(MPESA_PLAYER_STORAGE_KEY);
      if (raw) {
        const playerInfo = JSON.parse(raw);
        if (playerInfo && playerInfo.PersonalId) {
          PaymentsService.setPlayerInfo(playerInfo);
        }
      }
    } catch (_) { /* corrupted / disabled storage — non-fatal */ }

    // Pull the deposit/withdraw verb from the stash; fall back to
    // "Transaction" if it's missing (e.g. the user landed here directly).
    let label = 'Transaction';
    try {
      const raw = sessionStorage.getItem(PENDING_PAYMENT_STORAGE_KEY);
      if (raw) {
        const ctx = JSON.parse(raw);
        if (ctx?.type === 'withdraw') label = 'Withdrawal';
        else if (ctx?.type === 'deposit') label = 'Deposit';
      }
    } catch (_) { /* see above */ }

    const config = TOAST_BY_STATUS[outcome];
    if (config) {
      ToastWrapper(config.kind, t(`${label} ${config.verb}`));
    }

    // Clear stashes so a reload doesn't replay the toast.
    try {
      sessionStorage.removeItem(PENDING_PAYMENT_STORAGE_KEY);
      sessionStorage.removeItem(MPESA_PLAYER_STORAGE_KEY);
    } catch (_) { /* see above */ }

    // Strip the status / reference query params from the URL without
    // navigating (avoids re-mounting the rest of Layout) so a manual
    // refresh after this point doesn't replay the flow.
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete(PAYMENT_STATUS_QUERY_PARAM);
      url.searchParams.delete(PAYMENT_REFERENCE_QUERY_PARAM);
      window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
    } catch (_) { /* see above */ }
  }, [t]);

  return null;
};

export default PaymentStatusListener;
